import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Save, X, Download } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/Modal';
import { downloadAsCsv } from '@/lib/csv-export';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';
import { useLabel } from '@/lib/labels';

export type MenuCategory = {
  menu_category_id: string;
  category_code: string;
  category_name: string;
  display_order: number;
  effective_from: string;
  effective_to: string | null;
};

/** コードの半角英数字・ハイフン・アンダースコアのみ許容 (バックエンドの halfWidthCodeSchema と同じ規則) */
const CODE_PATTERN = '[A-Za-z0-9_-]+';

type EditingRow = {
  menu_category_id: string;
  category_name: string;
  display_order: number;
};

export function MenuCategoriesPage() {
  const t = useLabel();
  const qc = useQueryClient();
  // v0.3 (S-11 DL権限管理): export:view権限が無いユーザーにはCSV出力ボタンを非表示にする
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const canExport = hasPermissionForFeatureAction('export', 'view');
  const { data: categories = [], isLoading } = useQuery<MenuCategory[]>({
    queryKey: ['menu-categories'],
    queryFn: () => apiFetch<MenuCategory[]>('/menu-categories'),
  });

  const [creating, setCreating] = useState<{ category_code: string; category_name: string; display_order: number } | null>(null);
  const [editing, setEditing] = useState<EditingRow | null>(null);
  const [deleting, setDeleting] = useState<MenuCategory | null>(null);

  const createMut = useMutation({
    mutationFn: (body: { category_code: string; category_name: string; display_order: number }) =>
      apiFetch<{ menu_category_id: string }>('/menu-categories', {
        method: 'POST', body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['menu-categories'] });
      setCreating(null);
      toast.success('カテゴリを追加しました');
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '追加に失敗しました');
    },
  });

  const updateMut = useMutation({
    mutationFn: (r: EditingRow) => apiFetch(`/menu-categories/${r.menu_category_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ category_name: r.category_name.trim(), display_order: r.display_order }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['menu-categories'] });
      setEditing(null);
      toast.success('カテゴリを更新しました');
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/menu-categories/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['menu-categories'] });
      const name = deleting?.category_name ?? '';
      setDeleting(null);
      toast.success(`カテゴリ「${name}」を削除しました`);
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました');
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-h1">{t('entity.menu_categories', 'カテゴリマスタ')}</h1>
          <p className="text-sm text-neutral-sub mt-1">
            メニューカテゴリの追加・名称変更・並び順の管理を行います
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canExport && (
          <button
            type="button"
            disabled={isLoading || categories.length === 0}
            aria-label="カテゴリ一覧をマスタ取込CSV形式で出力"
            onClick={() => {
              const headers = ['category_code', 'category_name', 'display_order', 'effective_from', 'effective_to'];
              const rows = categories.map((c) => ({
                category_code: c.category_code,
                category_name: c.category_name,
                display_order: c.display_order,
                effective_from: c.effective_from,
                effective_to: c.effective_to ?? '',
              }));
              downloadAsCsv(`menu_categories_${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
            }}
            className="flex items-center gap-1 px-3 py-2 rounded border border-neutral-line text-sm hover:bg-neutral-panel disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            title={
              isLoading ? '読み込み中…'
                : categories.length === 0 ? '出力対象がありません'
                : 'マスタ取込 CSV 形式で出力 (再取込可)'
            }
          >
            <Download size={14} /> CSV出力
          </button>
          )}
          <button
            onClick={() => setCreating({ category_code: '', category_name: '', display_order:
              (categories.reduce((max, c) => Math.max(max, c.display_order), 0) + 10) })}
            className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover whitespace-nowrap"
          >
            <Plus size={16} /> 新規カテゴリ
          </button>
        </div>
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th scope="col" className="text-left px-4 py-2 w-28">{t('field.menu_categories.category_code', 'コード')}</th>
                <th scope="col" className="text-left px-4 py-2">{t('field.menu_categories.category_name', 'カテゴリ名')}</th>
                <th scope="col" className="text-right px-4 py-2 w-24">{t('field.menu_categories.display_order', '表示順')}</th>
                <th scope="col" className="w-24 px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => {
                const isEditing = editing?.menu_category_id === c.menu_category_id;
                return (
                  <tr key={c.menu_category_id} className="border-t border-neutral-line">
                    <td className="px-4 py-2 font-mono text-xs text-neutral-sub whitespace-nowrap">{c.category_code}</td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input value={editing.category_name}
                          onChange={(e) => setEditing({ ...editing, category_name: e.target.value })}
                          className="w-full px-2 py-1 rounded border border-neutral-line focus-ring" />
                      ) : c.category_name}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {isEditing ? (
                        <input type="number" min={0} max={9999} value={editing.display_order}
                          onChange={(e) => setEditing({ ...editing, display_order: Number(e.target.value) })}
                          className="w-20 px-2 py-1 rounded border border-neutral-line text-right focus-ring" />
                      ) : c.display_order}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button type="button" aria-label="保存"
                            onClick={() => updateMut.mutate(editing)}
                            disabled={updateMut.isPending || editing.category_name.trim() === ''}
                            className="p-1 text-brand-red hover:text-brand-red-hover disabled:opacity-40">
                            <Save size={14} />
                          </button>
                          <button type="button" aria-label="キャンセル"
                            onClick={() => setEditing(null)}
                            className="ml-1 p-1 text-neutral-sub hover:text-neutral-text">
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" aria-label="編集"
                            onClick={() => setEditing({
                              menu_category_id: c.menu_category_id,
                              category_name: c.category_name,
                              display_order: c.display_order,
                            })}
                            className="p-1 text-brand-red hover:text-brand-red-hover">
                            <Pencil size={14} />
                          </button>
                          <button type="button" aria-label="削除"
                            onClick={() => setDeleting(c)}
                            className="ml-1 p-1 text-neutral-sub hover:text-warning-text">
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {categories.length === 0 && (
            <EmptyState
              message="カテゴリが登録されていません"
              description="「＋ 新規カテゴリ」から追加してください"
            />
          )}
        </div>
      )}

      {/* 新規作成モーダル */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setCreating(null)}>
          <form onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              const code = creating.category_code.trim();
              const name = creating.category_name.trim();
              if (code === '') { toast.error('カテゴリコードを入力してください'); return; }
              if (name === '') { toast.error('カテゴリ名を入力してください'); return; }
              createMut.mutate({ category_code: code, category_name: name, display_order: creating.display_order });
            }}
            className="bg-white rounded-lg p-6 w-[420px] max-w-full space-y-3">
            <h2 className="text-h3">新規カテゴリ</h2>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.menu_categories.category_code', 'カテゴリコード')}*</label>
              <input required autoFocus value={creating.category_code}
                onChange={(e) => setCreating({ ...creating, category_code: e.target.value })}
                pattern={CODE_PATTERN}
                placeholder="例：CAT-005"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              <p className="text-xs text-neutral-sub mt-1">半角英数字・ハイフン・アンダースコアのみ。作成後は変更できません</p>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.menu_categories.category_name', 'カテゴリ名')}*</label>
              <input required value={creating.category_name}
                onChange={(e) => setCreating({ ...creating, category_name: e.target.value })}
                placeholder="例：ドリンク、デザート"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.menu_categories.display_order', '表示順')}（昇順）</label>
              <input type="number" min={0} max={9999} value={creating.display_order}
                onChange={(e) => setCreating({ ...creating, display_order: Number(e.target.value) })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              <p className="text-xs text-neutral-sub mt-1">数値が小さいほど上に表示されます</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setCreating(null)}
                className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
              <button type="submit" disabled={createMut.isPending}
                className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
                {createMut.isPending ? '作成中…' : '作成'}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="カテゴリを削除しますか？"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.menu_category_id); }}
        message={deleting && (
          <div className="space-y-2">
            <p>以下のカテゴリを削除します。この操作は<strong className="text-danger-text">取り消せません</strong>。</p>
            <div className="p-3 rounded bg-neutral-panel border border-neutral-line text-sm space-y-1">
              <div><span className="text-neutral-sub">コード：</span><span className="font-mono">{deleting.category_code}</span></div>
              <div><span className="text-neutral-sub">カテゴリ名：</span><span className="font-medium">{deleting.category_name}</span></div>
            </div>
            <p className="text-xs text-neutral-sub">
              ※ 既にメニューで使用されているカテゴリは削除できません（サーバー側でチェックされます）。
            </p>
          </div>
        )}
      />
    </div>
  );
}
